import { ComponentType, ComponentInstance } from "../../js/component";

class Accordion extends ComponentInstance {
  #savedAsOpen;

  static openClass = "accordion--open";
  static animateClass = "accordion--animate";

  shouldDispatchEvents = true;

  init() {
    this.button = this.el.querySelector(".accordion--title");
    this.contentContainer = this.el.querySelector(".accordion--content");
    this.focusableDescendants = this.contentContainer.querySelectorAll(
      ":is(input, select, textarea, button, object):not(:disabled), a:is([href]), [tabindex]",
    );

    // Make descendants unfocusable when collapsed.
    this.focusableDescendants.forEach((el) => {
      el.tabIndex = el.tabIndex || 0;
      el.dataset.originalTabIndex = el.tabIndex;
    });

    // Initial state from data attr set by Twig.
    this.isOpen = this.el.dataset.openByDefault === "true";
    // this.measureNaturalHeight();

    this.el.classList.remove(Accordion.animateClass);

    this.button.addEventListener("click", () => {
      this.isOpen = !this.isOpen;
    });

    this.el.classList.add("accordion--js");
    void this.el.offsetHeight; // reflow;
    this.el.classList.add(Accordion.animateClass);

    this._onHashChange = this.applyHashOpen.bind(this);

    window.addEventListener("hashchange", this._onHashChange);
    this.applyHashOpen();
  }

  remove() {
    if (this._onHashChange) {
      window.removeEventListener("hashchange", this._onHashChange);
      this._onHashChange = null;
    }
  }

  set isOpen(val) {
    const wasOpen = this.#savedAsOpen === true;
    this.#savedAsOpen = !!val;

    if (this.#savedAsOpen) {
      this.el.classList.add(Accordion.openClass);
      this.focusableDescendants.forEach((el) => (el.tabIndex = el.dataset.originalTabIndex));
      this.button.setAttribute("aria-expanded", "true");

      if (this.shouldDispatchEvents && !wasOpen) {
        // Bubble so the wrapper can enforce single-open.
        this.el.dispatchEvent(new Event("accordionopen", { bubbles: true }));
      }
    } else {
      this.el.classList.remove(Accordion.openClass);
      this.focusableDescendants.forEach((el) => (el.tabIndex = -1));
      this.button.setAttribute("aria-expanded", "false");
    }
  }

  get isOpen() {
    return this.#savedAsOpen;
  }

  measureNaturalHeight() {
    this.shouldDispatchEvents = false;
    const previousState = this.isOpen;
    this.el.classList.remove(Accordion.animateClass);
    this.isOpen = true;
    const height = this.contentContainer.getBoundingClientRect().height;
    this.el.style.setProperty("--natural-height", `${height}px`);
    this.isOpen = previousState;
    this.el.classList.add(Accordion.animateClass);
    this.shouldDispatchEvents = true;
    this.el.offsetHeight;
  }

  isItemOpen(item) {
    const btn = item?.querySelector(".accordion--title");
    return btn ? btn.getAttribute("aria-expanded") === "true" : false;
  }

  openItem(item, { scrollIntoView = true } = {}) {
    if (!item) return;
    const btn = item.querySelector(".accordion--title");
    if (!btn) return;

    // If not already open, click the button to go through the instance setter.
    if (!this.isItemOpen(item)) {
      btn.click();
    }

    if (scrollIntoView) {
      // Defer to let layout settle (especially if opening triggers transitions)
      requestAnimationFrame(() => {
        item.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }

  findAccordionItemFromHash(hash) {
    console.log(hash);
    if (!hash) return null;
    const id = hash.startsWith("#") ? hash.slice(1) : hash;

    // 1) Direct match by id
    let target = document.getElementById(id);

    // 2) If not found, try "<id>-panel"
    if (!target) {
      const tryPanel = document.getElementById(`${id}-panel`);
      if (tryPanel) target = tryPanel;
    }

    // If still not found, nothing to do.
    if (!target) return null;

    // Find the closest accordion item wrapper
    return target.closest(".accordion");
  }

  applyHashOpen() {
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    const item = this.findAccordionItemFromHash(hash);
    if (!item) return;

    this.openItem(item);
  }
}

new ComponentType(Accordion, "accordion", ".accordion");
